/**
 * Waitlist Service
 *
 * Handles iOS app waitlist signups, status checks, and updates.
 * Data is stored both in the user profile (for auth flow) and
 * a separate waitlist collection (for admin analytics).
 */

import { db } from '../config/firebase';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs
} from 'firebase/firestore';

// Dropdown options - editable server-side later
export const USE_CASE_OPTIONS = [
  { value: 'client_meetings', label: 'Client meetings' },
  { value: 'internal_team', label: 'Internal team meetings' },
  { value: 'sales_calls', label: 'Sales calls' },
  { value: 'interviews', label: 'Interviews' },
  { value: 'coaching', label: 'Coaching / consulting' },
  { value: 'education', label: 'Education / tutoring' },
  { value: 'community', label: 'Community or group calls' },
  { value: 'other', label: 'Other' }
];

export const INDUSTRY_OPTIONS = [
  { value: 'tech', label: 'Tech / Software' },
  { value: 'agency', label: 'Agency / Creative' },
  { value: 'education', label: 'Education' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'finance', label: 'Finance' },
  { value: 'legal', label: 'Legal' },
  { value: 'real_estate', label: 'Real estate' },
  { value: 'startup', label: 'Startup / Founder' },
  { value: 'freelance', label: 'Freelance' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'other', label: 'Other' }
];

export const TEAM_SIZE_OPTIONS = [
  { value: 'solo', label: 'Just me' },
  { value: '2-5', label: '2-5 people' },
  { value: '6-20', label: '6-20 people' },
  { value: '21-50', label: '21-50 people' },
  { value: '51-200', label: '51-200 people' },
  { value: '200+', label: '200+ people' }
];

export const ROLE_OPTIONS = [
  { value: 'founder', label: 'Founder / CEO' },
  { value: 'manager', label: 'Manager / Team Lead' },
  { value: 'freelancer', label: 'Freelancer' },
  { value: 'employee', label: 'Employee' },
  { value: 'student', label: 'Student' },
  { value: 'other', label: 'Other' }
];

/**
 * Submit a waitlist entry
 *
 * @param {Object} data - Waitlist form data
 * @param {string} data.firstName - Required first name
 * @param {string} data.lastName - Required last name
 * @param {string} data.email - Required email
 * @param {string} data.usageType - Required usage type (personal/professional)
 * @param {string} data.useCase - Required use case
 * @param {string} data.industry - Required industry
 * @param {string} [data.notes] - Optional notes
 * @param {string} [data.companyName] - Optional company name
 * @param {string} [data.teamSize] - Optional team size
 * @param {string} [data.role] - Optional role
 * @param {string} [userId] - Optional user ID if logged in
 * @returns {Promise<{id: string, status: string}>}
 */
export async function submitWaitlistEntry(data, userId = null) {
  const {
    firstName,
    lastName,
    email,
    usageType,
    useCase,
    industry,
    notes = '',
    companyName = '',
    teamSize = '',
    role = ''
  } = data;

  if (!firstName || !lastName || !email || !usageType || !useCase || !industry) {
    throw new Error('First name, last name, email, usage type, use case, and industry are required');
  }

  const waitlistData = {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    email: email.toLowerCase().trim(),
    usageType, // personal or professional
    useCase,
    industry,
    notes: notes.trim(),
    companyName: companyName.trim(),
    teamSize,
    role,
    source: 'web',
    status: 'pending', // pending | approved | rejected
    submittedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    userId: userId || null,
    notifyOnApproval: true
  };

  // Add to waitlist collection for admin analytics
  const waitlistRef = await addDoc(collection(db, 'waitlist'), waitlistData);
  console.log('Waitlist entry created:', waitlistRef.id);

  // If user is logged in, also update their profile
  if (userId) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        waitlistStatus: 'pending',
        waitlistId: waitlistRef.id,
        waitlistData: {
          firstName: waitlistData.firstName,
          lastName: waitlistData.lastName,
          email: waitlistData.email,
          useCase,
          industry,
          notes: waitlistData.notes,
          companyName: waitlistData.companyName,
          teamSize,
          role,
          submittedAt: serverTimestamp()
        }
      });
    } catch (profileError) {
      console.error('Failed to update user profile with waitlist data:', profileError);
      // Don't fail the whole submission if profile update fails
    }
  }

  return {
    id: waitlistRef.id,
    status: 'pending'
  };
}

/**
 * Check if an email is already on the waitlist
 *
 * @param {string} email - Email to check
 * @param {boolean} isAuthenticated - Whether the user is logged in
 * @returns {Promise<{exists: boolean, status: string | null}>}
 */
export async function checkWaitlistEmail(email, isAuthenticated = false) {
  const normalizedEmail = email.toLowerCase().trim();

  try {
    const q = query(
      collection(db, 'waitlist'),
      where('email', '==', normalizedEmail)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return { exists: false, status: null };
    }

    const entry = snapshot.docs[0].data();
    return {
      exists: true,
      status: entry.status,
      id: snapshot.docs[0].id
    };
  } catch (error) {
    // Non-authenticated users can't query the waitlist collection
    // Return false to let them proceed - duplicates will be caught server-side
    if (error.code === 'permission-denied' && !isAuthenticated) {
      console.log('Waitlist check skipped for non-authenticated user');
      return { exists: false, status: null, unchecked: true };
    }
    throw error;
  }
}

/**
 * Get waitlist status for a user
 *
 * @param {string} userId - User ID
 * @returns {Promise<{status: string, data: Object} | null>}
 */
export async function getWaitlistStatus(userId) {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    return null;
  }

  const userData = userSnap.data();

  if (!userData.waitlistStatus) {
    return null;
  }

  return {
    status: userData.waitlistStatus,
    data: userData.waitlistData || null,
    waitlistId: userData.waitlistId || null
  };
}

/**
 * Update waitlist entry for a user
 *
 * @param {string} userId - User ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export async function updateWaitlistEntry(userId, updates) {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists() || !userSnap.data().waitlistId) {
    throw new Error('No waitlist entry found for this user');
  }

  const waitlistId = userSnap.data().waitlistId;

  // Update waitlist collection
  const waitlistRef = doc(db, 'waitlist', waitlistId);
  await updateDoc(waitlistRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });

  // Update user profile
  await updateDoc(userRef, {
    'waitlistData.useCase': updates.useCase || userSnap.data().waitlistData?.useCase,
    'waitlistData.industry': updates.industry || userSnap.data().waitlistData?.industry,
    'waitlistData.notes': updates.notes ?? userSnap.data().waitlistData?.notes,
    'waitlistData.companyName': updates.companyName ?? userSnap.data().waitlistData?.companyName,
    'waitlistData.teamSize': updates.teamSize || userSnap.data().waitlistData?.teamSize,
    'waitlistData.role': updates.role || userSnap.data().waitlistData?.role
  });
}

/**
 * Toggle email notification preference
 *
 * @param {string} userId - User ID
 * @param {boolean} notify - Whether to notify on approval
 * @returns {Promise<void>}
 */
export async function toggleNotifyOnApproval(userId, notify) {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists() || !userSnap.data().waitlistId) {
    throw new Error('No waitlist entry found for this user');
  }

  const waitlistId = userSnap.data().waitlistId;
  const waitlistRef = doc(db, 'waitlist', waitlistId);

  await updateDoc(waitlistRef, {
    notifyOnApproval: notify,
    updatedAt: serverTimestamp()
  });
}

/**
 * Link a waitlist entry to a user account (after signup)
 *
 * @param {string} userId - User ID
 * @param {string} email - User email to find existing entry
 * @returns {Promise<boolean>} - Whether an existing entry was linked
 */
export async function linkWaitlistToUser(userId, email) {
  const existing = await checkWaitlistEmail(email);

  if (!existing.exists) {
    return false;
  }

  // Update waitlist entry with user ID
  const waitlistRef = doc(db, 'waitlist', existing.id);
  const waitlistSnap = await getDoc(waitlistRef);
  const waitlistData = waitlistSnap.data();

  await updateDoc(waitlistRef, {
    userId,
    updatedAt: serverTimestamp()
  });

  // Update user profile with waitlist data
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    waitlistStatus: waitlistData.status,
    waitlistId: existing.id,
    waitlistData: {
      firstName: waitlistData.firstName,
      lastName: waitlistData.lastName,
      email: waitlistData.email,
      useCase: waitlistData.useCase,
      industry: waitlistData.industry,
      notes: waitlistData.notes,
      companyName: waitlistData.companyName,
      teamSize: waitlistData.teamSize,
      role: waitlistData.role,
      submittedAt: waitlistData.submittedAt
    }
  });

  return true;
}
